import { theme, Tooltip, Typography } from 'antd';
import type { GlobalToken } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DayStatus } from './calendarUtils';
import { toDateKey } from './calendarUtils';

// The first / last day of a semester, or of a saved study year (shown like the first semester).
// The start / end of the study year, while it is chosen, and its end after the second semester have their own colour
export type PeriodBoundary = {
  label: string;
  semester: 'first' | 'second' | 'studyYear';
};

type MonthCalendarProps = {
  year: number;
  month: number; // 0 = January
  getDayStatus: (date: Date) => DayStatus;
  // Without it the days can't be clicked, e.g. for a saved study year
  onDayClick?: (date: Date) => void;
  getHolidayName: (date: Date) => string | undefined;
  // The first / last day of a semester, undefined for other days
  getPeriodBoundary: (date: Date) => PeriodBoundary | undefined;
  // Whether a day can be clicked when onDayClick is given
  canClickDay: (date: Date) => boolean;
  // Called when the mouse moves onto a clickable day
  onDayHover?: (date: Date) => void;
  // Days of the period or interval being chosen, shown as a grey ghost
  isInRangePreview?: (date: Date) => boolean;
  // The days of another study year (e.g. the next one, in the last September) are shown faded
  isInStudyYear?: (date: Date) => boolean;
  // The teaching days of the second semester are green, those of the first one blue
  isInSecondSemester?: (date: Date) => boolean;
};

// The space between the days, so each day stands apart as its own tile
const DAY_GAP = 6;

// Week starts on Monday: 2024-01-01 was a Monday
const WEEKDAY_REFERENCE_DATES = Array.from(
  { length: 7 },
  (_, i) => new Date(2024, 0, 1 + i)
);

export const getDayStatusStyle = (
  status: DayStatus,
  token: GlobalToken
): React.CSSProperties => {
  switch (status) {
    case 'working':
      return { background: token.blue2, color: token.blue9 };
    case 'free':
      return { background: token.colorErrorBg, color: token.colorError };
    case 'exam':
      return { background: token.orange2, color: token.orange9 };
    case 'retake':
      return { background: token.purple2, color: token.purple9 };
    // its own colour, so the time outside of the teaching period, the summer holiday, stands out
    // and isn't mistaken for the grey preview of a selection
    case 'outside':
      return { background: token.green2, color: token.green9 };
  }
};

// A teaching day of the second semester: a pale shade of its start / end's teal,
// like the first semester's pale blue, and unlike the green summer holiday
export const getSecondSemesterWorkingStyle = (
  token: GlobalToken
): React.CSSProperties => ({
  background: token.cyan3,
  color: token.cyan9,
});

// A public holiday is a free day too, but in a deeper red, so it can be told apart from the other free days
export const getHolidayStyle = (token: GlobalToken): React.CSSProperties => ({
  background: token.red3,
  color: token.red9,
});

// The first and last day of the study year stand out with a solid colour
// Today gets a green ring, so its working / free colour stays visible
export const getTodayStyle = (token: GlobalToken): React.CSSProperties => ({
  boxShadow: `inset 0 0 0 2px ${token.green6}`,
  fontWeight: 700,
});

// Only the number, faded, so it is clear these days belong to another study year
export const getOtherStudyYearStyle = (
  token: GlobalToken
): React.CSSProperties => ({
  background: 'transparent',
  color: token.colorTextQuaternary,
});

// Grey with a dashed border over the colours of the days, so they look selected until the click
export const getRangePreviewStyle = (
  token: GlobalToken
): React.CSSProperties => ({
  background: token.colorFillSecondary,
  color: token.colorText,
  outline: `1px dashed ${token.colorTextTertiary}`,
  outlineOffset: -1,
});

// The two semesters and the end of the study year have different colours, so they can be told apart
export const getPeriodBoundaryStyle = (
  token: GlobalToken,
  semester: PeriodBoundary['semester']
): React.CSSProperties => ({
  background:
    semester === 'first'
      ? token.colorPrimary
      : semester === 'second'
        ? token.cyan8
        : token.magenta7,
  color: token.colorWhite,
  fontWeight: 700,
});

export const MonthCalendar = ({
  year,
  month,
  getDayStatus,
  onDayClick,
  getHolidayName,
  getPeriodBoundary,
  canClickDay,
  onDayHover,
  isInRangePreview,
  isInStudyYear,
  isInSecondSemester,
}: MonthCalendarProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const todayKey = toDateKey(new Date());
  const { token } = theme.useToken();

  const monthName = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, { month: 'long' }).format(
        new Date(year, month, 1)
      ),
    [i18n.language, year, month]
  );

  const weekdayNames = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language, {
      weekday: 'narrow',
    });
    return WEEKDAY_REFERENCE_DATES.map((date) => formatter.format(date));
  }, [i18n.language]);

  // Empty cells before the 1st, so the 1st lands under the right weekday
  const cells = useMemo(() => {
    const leadingEmptyCells = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return [
      ...Array.from({ length: leadingEmptyCells }, () => null),
      ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];
  }, [year, month]);

  return (
    <div style={{ padding: '0 8px' }}>
      <Typography.Title
        level={4}
        style={{
          textAlign: 'center',
          fontStyle: 'italic',
          fontWeight: 400,
          textTransform: 'capitalize',
          marginBottom: 12,
        }}
      >
        {monthName} {year}
      </Typography.Title>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          // the same gap as the days, so the weekdays stay above their columns
          columnGap: DAY_GAP,
          textAlign: 'center',
          fontSize: 12,
          fontWeight: 600,
          color: token.colorTextSecondary,
          borderBottom: `1px solid ${token.colorBorder}`,
          paddingBottom: 6,
          marginBottom: 6,
        }}
      >
        {weekdayNames.map((name, index) => (
          <span key={index}>{name.toUpperCase()}</span>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: DAY_GAP,
          textAlign: 'center',
          fontSize: 13,
        }}
      >
        {cells.map((day, index) => {
          if (day === null) {
            return <span key={index} />;
          }
          const date = new Date(year, month, day);
          const status = getDayStatus(date);
          const isClickable = onDayClick !== undefined && canClickDay(date);
          const holidayName = getHolidayName(date);
          const boundary = getPeriodBoundary(date);
          const isToday = toDateKey(date) === todayKey;
          const isInOtherStudyYear = isInStudyYear?.(date) === false;
          const tooltip = [
            isToday ? t('academicCalendar.today') : undefined,
            isInOtherStudyYear
              ? t('academicCalendar.otherStudyYear')
              : undefined,
            boundary?.label,
            holidayName,
          ]
            .filter(Boolean)
            .join(' · ');
          const dayCell = (
            <span
              key={index}
              aria-label={tooltip ? `${day}, ${tooltip}` : undefined}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onClick={isClickable ? () => onDayClick?.(date) : undefined}
              onMouseEnter={isClickable ? () => onDayHover?.(date) : undefined}
              onKeyDown={
                isClickable
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onDayClick?.(date);
                      }
                    }
                  : undefined
              }
              style={{
                lineHeight: '32px',
                borderRadius: token.borderRadius,
                cursor: isClickable ? 'pointer' : 'default',
                userSelect: 'none',
                fontWeight: holidayName ? 700 : undefined,
                textDecoration: holidayName ? 'underline dotted' : undefined,
                ...getDayStatusStyle(status, token),
                ...(status === 'working' && isInSecondSemester?.(date)
                  ? getSecondSemesterWorkingStyle(token)
                  : {}),
                // only while it is free: a holiday the rector made a working day, or one in the summer, keeps that colour
                ...(holidayName && status === 'free'
                  ? getHolidayStyle(token)
                  : {}),
                ...(isInOtherStudyYear ? getOtherStudyYearStyle(token) : {}),
                ...(isInRangePreview?.(date)
                  ? getRangePreviewStyle(token)
                  : {}),
                ...(boundary
                  ? getPeriodBoundaryStyle(token, boundary.semester)
                  : {}),
                ...(isToday ? getTodayStyle(token) : {}),
              }}
            >
              {day}
            </span>
          );
          return tooltip ? (
            <Tooltip key={index} title={tooltip}>
              {dayCell}
            </Tooltip>
          ) : (
            dayCell
          );
        })}
      </div>
    </div>
  );
};
